import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { TASK_STATUS, PRIORITY, label } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Loader2, Play, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

function AssignTaskDialog({ open, onOpenChange, onSaved }) {
  const { users } = useAuth();
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const [f, setF] = useState({ title: "", assigned_to: "", client_id: "", due_date: "", priority: "medium", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  React.useEffect(() => { if (open) setF({ title: "", assigned_to: "", client_id: "", due_date: "", priority: "medium", notes: "" }); }, [open]);

  const save = async () => {
    if (!f.title.trim()) return toast.error("Task title required");
    if (!f.assigned_to) return toast.error("Select an employee");
    setSaving(true);
    try {
      await api.post("/tasks", { ...f, client_id: f.client_id || null, due_date: f.due_date || null });
      toast.success("Task assigned"); onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Assign Task</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs text-zinc-500">Task Title *</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1" placeholder="e.g. Make Hotel Mayur Anniversary Invite" data-testid="task-title-input" autoFocus /></div>
          <div>
            <Label className="text-xs text-zinc-500">Assign To *</Label>
            <Select value={f.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
              <SelectTrigger className="mt-1" data-testid="task-assignee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>{users.filter((u) => u.active).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-500">Due Date</Label><Input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1" data-testid="task-due-input" /></div>
            <div>
              <Label className="text-xs text-zinc-500">Priority</Label>
              <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Client (optional)</Label>
            <Select value={f.client_id || "none"} onValueChange={(v) => set("client_id", v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-zinc-500">Notes (optional)</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="task-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Assign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Tasks() {
  const { user, users, isManager } = useAuth();
  const qc = useQueryClient();
  const umap = {}; users.forEach((u) => (umap[u.id] = u));
  const cmapQ = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const cmap = {}; (cmapQ.data || []).forEach((c) => (cmap[c.id] = c.name));
  const [view, setView] = useState("all");
  const [dialog, setDialog] = useState(false);
  const [priority, setPriority] = useState("all");

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["tasks", view],
    queryFn: async () => (await api.get("/tasks", { params: { view: view === "all" ? undefined : view } })).data,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["tasks"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };
  const setStatus = async (id, status) => { await api.put(`/tasks/${id}`, { status }); toast.success("Updated"); refresh(); };
  const del = async (id) => { await api.delete(`/tasks/${id}`); toast.success("Deleted"); refresh(); };

  let rows = tasks;
  if (view === "mine") rows = rows.filter((t) => t.assigned_to === user?.id);
  if (priority !== "all") rows = rows.filter((t) => t.priority === priority);

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Tasks</h1>
          <p className="mt-1 text-sm text-zinc-500">General work assigned by management — separate from content.</p>
        </div>
        {isManager && <Button onClick={() => setDialog(true)} data-testid="assign-task-button"><Plus className="mr-2 h-4 w-4" />Assign Task</Button>}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={view} onValueChange={setView}>
          <TabsList data-testid="tasks-tabs">
            <TabsTrigger value="all">{isManager ? "Team Tasks" : "My Tasks"}</TabsTrigger>
            {isManager && <TabsTrigger value="mine">My Tasks</TabsTrigger>}
            <TabsTrigger value="overdue">Overdue</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Priority</SelectItem>{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden rounded-lg border-zinc-200 shadow-sm">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-zinc-100 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                <th className="px-4 py-3 text-left font-medium">Task</th>
                <th className="px-4 py-3 text-left font-medium">Assigned To</th>
                <th className="px-4 py-3 text-left font-medium">Client</th>
                <th className="px-4 py-3 text-left font-medium">Due</th>
                <th className="px-4 py-3 text-left font-medium">Priority</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} data-testid={`task-row-${t.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                    <td className="px-4 py-3 font-medium text-zinc-900">{t.title}</td>
                    <td className="px-4 py-3 text-zinc-600">{umap[t.assigned_to]?.name || "—"}</td>
                    <td className="px-4 py-3 text-zinc-600">{cmap[t.client_id] || "—"}</td>
                    <td className="px-4 py-3 text-zinc-600">{fmtDate(t.due_date)}</td>
                    <td className="px-4 py-3"><Badge color={PRIORITY[t.priority]?.color}>{label(PRIORITY, t.priority)}</Badge></td>
                    <td className="px-4 py-3"><Badge color={TASK_STATUS[t.display_status]?.color} dot>{label(TASK_STATUS, t.display_status)}</Badge></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {t.status === "todo" && (t.assigned_to === user?.id || isManager) && <Button size="sm" variant="ghost" onClick={() => setStatus(t.id, "in_progress")}><Play className="h-3.5 w-3.5" /></Button>}
                        {t.status !== "completed" && (t.assigned_to === user?.id || isManager) && <Button size="sm" variant="ghost" onClick={() => setStatus(t.id, "completed")} data-testid={`complete-task-${t.id}`}><Check className="h-3.5 w-3.5" /></Button>}
                        {isManager && <Button size="sm" variant="ghost" onClick={() => del(t.id)} className="text-red-500"><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-zinc-400">No tasks here</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AssignTaskDialog open={dialog} onOpenChange={setDialog} onSaved={refresh} />
    </div>
  );
}
